import { UseQueryOptions } from "@tanstack/react-query"
import { OK, ResourceResult } from "helpers/data-statuses"
import { IsoDate } from "helpers/types/dates"
import { SelectedInstrument } from "helpers/types/instruments/selected"
import { compact, get } from "lodash"
import { useMemo } from "react"
import { useDispatch } from "react-redux"
import { useAccountAlias } from "resources/account"
import { Creators } from "model/apiWrapper"
import { Uuid } from "helpers/types/uuid"
import { Dispatch } from "redux"
import { useNullableQuery } from "./queries"

interface QueryResult {
  data?: {
    portfolio: {
      dates: {
        date: IsoDate
        startOfDayEquity?: number
      }[]
      perSecurityPnl: {
        date: IsoDate
      }[]
    }
  }
}

const PORTFOLIO_PNL_SOD_QUERY = `query ($portfolioId: String!, $from: Date!, $to: Date!) {
  portfolio(id: $portfolioId) {
    availableFrom
    dates(from: $from, to: $to) {
      startOfDayEquity
    }
    perSecurityPnl(from: $from, to: $to) {
      date
    }
  }
}`

function createPortfolioPnlSODQuery(dispatch: Dispatch, account: string, portfolioId: Uuid | string | null, startDate: IsoDate, isPortfolio: boolean, isPerformance: boolean): UseQueryOptions<QueryResult, Error, boolean> | null {
  if (!portfolioId || !startDate || !isPortfolio || !isPerformance) {
    return null
  }

  const variables = {
    portfolioId,
    from: startDate,
    to: startDate,
  }

  return {
    queryKey: compact(["portfolio-pnl-sod-availability", portfolioId, startDate]),
    queryFn: () => new Promise((resolve, reject) => {
      dispatch(Creators.graphql(account, PORTFOLIO_PNL_SOD_QUERY, variables, resolve, reject))
    }),
    select: (graphqlResult: QueryResult) => {
      const hasStartOfDayEquity = get(graphqlResult, "data.portfolio.dates.0.startOfDayEquity", null) !== null
      const hasPnl = get(graphqlResult, "data.portfolio.perSecurityPnl.length", 0) > 0

      return !hasStartOfDayEquity || !hasPnl
    },
    enabled: true,
  }
}

export function useShouldAdjustDates(positionSet: SelectedInstrument, startDate: IsoDate, isPerformance = true): ResourceResult<boolean> {
  const dispatch = useDispatch()
  const account = useAccountAlias()

  // [TODO:TS] Remove `get`s once all callers converted to typescript
  const portfolioId = get(positionSet, "id", null)
  const isPortfolio = get(positionSet, "type", null) === "PORTFOLIO"

  const query = useMemo(() => createPortfolioPnlSODQuery(
    dispatch,
    account,
    portfolioId,
    startDate,
    isPortfolio,
    isPerformance,
  ), [dispatch, account, portfolioId, startDate, isPortfolio, isPerformance])

  const result = useNullableQuery(query)

  // If query is not enabled for any reason, just look at isPerformance to know if we should adjust dates
  if (!query) {
    return [isPerformance, OK]
  }

  return result
}
